import { useState } from 'react';
import { FileText, Play, Mic, ChevronRight, Edit2, Trash2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { VoiceMemoPlayer } from './voice-memo-player';
import { VoiceMemoStatusBadge } from './voice-memo-status-badge';
import { TitleEditModal } from '@/components/ui/title-edit-modal';
import { updateVoiceMemoTitle, fetchWithAuth } from '@/lib/api';
import { useQueryClient } from '@tanstack/react-query';
import type { VoiceMemo } from '@/lib/api';

interface VoiceMemoCardProps {
  memo: VoiceMemo;
  onProcess?: (filename: string) => void;
  onRemove?: (id: string) => void;
  onViewTranscript?: (memo: VoiceMemo) => void;
  onReviewAndFile?: (memo: VoiceMemo) => void;
  isSelected?: boolean;
  viewMode?: 'card' | 'grid';
}

export function VoiceMemoCard({ memo, onProcess, onRemove, onViewTranscript, onReviewAndFile, isSelected = false, viewMode = 'card' }: VoiceMemoCardProps) {
  const isGrid = viewMode === 'grid';
  const [isTitleModalOpen, setIsTitleModalOpen] = useState(false);
  const queryClient = useQueryClient();

  const handleSaveTitle = async (title: string, summary?: string) => {
    await updateVoiceMemoTitle(memo.id, title, summary);
    queryClient.invalidateQueries({ queryKey: ['voice-memos'] });
  };

  const handleGenerateAI = async (mode: 'both' | 'title-only' | 'summary-only' = 'both') => {
    const response = await fetchWithAuth(`/api/voice-memos/${memo.id}/generate-title`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ mode }),
    });

    if (!response.ok) {
      throw new Error('Failed to generate');
    }

    const data = await response.json();
    return { title: data.title, summary: data.summary };
  };

  return (
    <Card className={`group p-4 flex flex-col h-full min-w-0 overflow-hidden transition-all duration-500 ease-in-out ${
      isSelected
        ? 'border-l-2 border-l-primary/50 border border-primary/20 bg-primary/10 hover:bg-primary/15'
        : 'hover:bg-muted/50'
    }`}>
      <div className="flex items-start gap-3 mb-3">
        <Mic className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4
              className="font-semibold text-sm leading-tight truncate"
              title={memo.title || memo.filename}
            >
              {memo.title || memo.filename}
            </h4>
            <Button
              size="sm"
              variant="ghost"
              className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 hover:opacity-100 transition-opacity"
              onClick={() => setIsTitleModalOpen(true)}
              title={memo.title ? 'Edit title' : 'Add title'}
            >
              <Edit2 className="h-3 w-3" />
            </Button>
            <VoiceMemoStatusBadge status={memo.status} />
          </div>
          {memo.title && (
            <div className="text-xs text-muted-foreground mb-1 truncate" title={memo.filename}>
              {memo.filename}
            </div>
          )}
          <div className={`flex items-center gap-1 text-xs text-muted-foreground mb-2 min-w-0 flex-shrink-0 ${isGrid ? 'flex-wrap' : 'flex-nowrap overflow-hidden'}`}>
            <span className="flex-shrink-0 whitespace-nowrap">{memo.date}</span>
            <span className="flex-shrink-0">•</span>
            <span className="flex-shrink-0 whitespace-nowrap">{memo.duration}</span>
            {memo.fileSize && (
              <>
                <span className="flex-shrink-0 hidden md:inline">•</span>
                <span className="flex-shrink-0 whitespace-nowrap hidden md:inline">{memo.fileSize}</span>
              </>
            )}
            {!isGrid && memo.transcript?.wordCount && (
              <>
                <span className="flex-shrink-0 hidden lg:inline">•</span>
                <span className="flex-shrink-0 whitespace-nowrap hidden lg:inline">{memo.transcript.wordCount} words</span>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="mb-3 flex-1">
        <VoiceMemoPlayer filename={memo.filename} duration={memo.duration} />
      </div>

      <div className="pt-3 border-t">
        {onRemove && (
          <div className="flex justify-end mb-2">
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs text-muted-foreground"
              onClick={() => onRemove(memo.id)}
              title="Remove from list"
            >
              <Trash2 className="h-3.5 w-3.5 mr-1" />
              Remove
            </Button>
          </div>
        )}
        {/* Show transcribe button if no transcript exists, regardless of status */}
        {!memo.transcript?.path && onProcess && (
          <div className="flex justify-end">
            <Button
              size="sm"
              variant="default"
              onClick={() => onProcess(memo.filename)}
            >
              <Play className="w-4 h-4 mr-2" />
              Transcribe
            </Button>
          </div>
        )}

        {/* Show view transcript/analysis buttons only if transcript exists */}
        {memo.transcript?.path && memo.status === 'transcribed' && (
          <button
            className="w-full flex items-center justify-between text-xs text-muted-foreground hover:text-foreground transition-colors"
            onClick={() => onViewTranscript?.(memo)}
          >
            <span>View transcript</span>
            <ChevronRight className="h-3 w-3" aria-hidden="true" />
          </button>
        )}

        {memo.transcript?.path && memo.status === 'analyzed' && (
          <button
            className="w-full flex items-center justify-between text-xs text-muted-foreground hover:text-foreground transition-colors"
            onClick={() => onReviewAndFile?.(memo)}
          >
            <span>View analysis</span>
            <ChevronRight className="h-3 w-3" aria-hidden="true" />
          </button>
        )}
      </div>

      <TitleEditModal
        open={isTitleModalOpen}
        onOpenChange={setIsTitleModalOpen}
        title={memo.title || ''}
        subtitle={memo.filename}
        summary={memo.summary || ''}
        showSummary={true}
        onSave={handleSaveTitle}
        onGenerateAI={memo.transcript?.path ? handleGenerateAI : undefined}
        placeholder="Enter a title for this voice memo"
        summaryPlaceholder="Enter a brief summary (optional)"
      />
    </Card>
  );
}

